'use client'

import { XIcon } from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/tailwind'
import { Button } from './button'

export interface FilterChip {
  id: string
  label: string
  onRemove: () => void
}

export interface FilterChipsProps {
  chips: FilterChip[]
  onClearAll?: () => void
  className?: string
}

/**
 * FilterChips component displays active filters as removable chips
 * with horizontal scrolling and optional Clear All button.
 *
 * Design features:
 * - Horizontal scroll for overflow filters
 * - Individual remove buttons (XIcon icon)
 * - Clear All button when multiple filters active
 * - 44px minimum touch targets on mobile
 * - Filter count display
 */
export function FilterChips({
  chips,
  onClearAll,
  className,
}: FilterChipsProps) {
  if (chips.length === 0) {
    return null
  }

  const showClearAll = chips.length > 1 && onClearAll

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Filter count */}
      <span className="text-muted-foreground shrink-0 text-sm">
        {chips.length === 1 ? '1 filter' : `${chips.length} filters`}
      </span>

      {/* Scrollable chips container */}
      <div className="scrollbar-hide flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
        <AnimatePresence mode="popLayout">
          {chips.map((chip) => (
            <motion.button
              key={chip.id}
              layout
              initial={{ scale: 0.8, opacity: 0, filter: 'blur(4px)' }}
              animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
              exit={{ scale: 0.8, opacity: 0, filter: 'blur(4px)' }}
              transition={{ duration: 0.2, type: 'spring', bounce: 0.1 }}
              onClick={chip.onRemove}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5',
                'min-h-[44px] sm:min-h-auto',
                'bg-foreground/10 text-foreground rounded-full px-3 py-1.5',
                'text-sm font-medium',
                'hover:bg-foreground/15 active:bg-foreground/20',
                'transition-colors',
                'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              )}
              aria-label={`Remove ${chip.label} filter`}
            >
              <span className="select-none">{chip.label}</span>
              <XIcon
                className="size-4 shrink-0"
                weight="bold"
                aria-hidden="true"
              />
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {/* Clear All button */}
      {showClearAll && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="shrink-0"
        >
          Clear All
        </Button>
      )}
    </div>
  )
}
